import { enablePredictiveTargetingApi } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useHasFlagEvaluated } from '@gonfalon/flags/hooks/useHasFlagEvaluated';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import {
  ButtonGroup,
  IconButton,
  Perceivable,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { ApprovalRequestResourceKind } from '#utils/approvals/types';
import { trackFlagTargetingEvent } from '#utils/flags/flagAnalytics';

import { useReviewAndSaveContext } from './reviewAndSaveContext';

interface ToolbarProps {
  resourceKind: ApprovalRequestResourceKind;
  resourceContext: {
    projectKey: string;
    resourceKey: string;
    environmentKey: string;
  };
  hasConflicts?: boolean;
  isApprovalRequest?: boolean;
  hideJsonDiff?: boolean;
  hideTestRun?: boolean;
}

export function Toolbar({
  resourceKind,
  resourceContext,
  isApprovalRequest = false,
  hasConflicts = false,
  hideJsonDiff = false,
  hideTestRun = false,
}: ToolbarProps) {
  const { isJsonDiffingEnabled, setIsJsonDiffingEnabled, expandAll, setExpandAll, testRunEnabled, setTestRunEnabled } =
    useReviewAndSaveContext();
  const { projectKey, resourceKey, environmentKey } = resourceContext;
  const isFlagConfiguration = resourceKind === 'FlagConfiguration';

  const flagHasEvaluated = useHasFlagEvaluated({
    projectKey,
    flagKey: resourceKey,
    environmentKey,
    enabled: isFlagConfiguration,
  });

  const description = expandAll
    ? msg('pendingChanges.reviewToolbar.collapseAll')
    : msg('pendingChanges.reviewToolbar.expandAll');

  return (
    <ButtonGroup>
      {!hideJsonDiff && (
        <ToggleButtonGroup
          selectionMode="single"
          selectedKeys={testRunEnabled ? [] : [isJsonDiffingEnabled ? 'json-editing' : 'ui-editing']}
          data-test-id="schema-editor-mode-button-group"
        >
          <ToggleButton
            size="small"
            onPress={() => {
              setIsJsonDiffingEnabled(false);
              setTestRunEnabled(false);
              trackFlagTargetingEvent('View Visual Button Clicked', {
                isApprovalRequest,
              });
            }}
            id="ui-editing"
            aria-label={msg('pendingChanges.reviewToolbar.visualLabel')}
            data-test-id="instruction-mode-button-visual"
          >
            <Icon size="tiny" name="clipboard-edit" />
            {msg('pendingChanges.reviewToolbar.visual')}
          </ToggleButton>
          <Perceivable isDisabled={hasConflicts}>
            <TooltipTrigger isDisabled={!hasConflicts}>
              <ToggleButton
                size="small"
                onPress={() => {
                  setIsJsonDiffingEnabled(true);
                  setTestRunEnabled(false);
                  trackFlagTargetingEvent('View JSON Button Clicked', {
                    isApprovalRequest,
                  });
                }}
                id="json-editing"
                aria-label={msg('pendingChanges.reviewToolbar.jsonLabel')}
                data-test-id="instruction-mode-button-json"
              >
                <Icon size="tiny" name="code-circle-outline" />
                {msg('pendingChanges.reviewToolbar.json')}
              </ToggleButton>
              <Tooltip placement="bottom">
                <Message id="pendingChanges.reviewToolbar.jsonConflictsTooltip" />
              </Tooltip>
            </TooltipTrigger>
          </Perceivable>
        </ToggleButtonGroup>
      )}

      <TooltipTrigger>
        <IconButton
          aria-label={description}
          icon={expandAll ? 'chevrons-in' : 'chevrons-out'}
          size="small"
          isDisabled={testRunEnabled || isJsonDiffingEnabled}
          onPress={() => {
            setExpandAll(!expandAll);
            // Analytics event name, not copy: rewording the tooltip must not rename the event.
            trackFlagTargetingEvent(expandAll ? 'Collapse all changes' : 'Expand all changes', {
              isApprovalRequest,
            });
          }}
        />
        <Tooltip placement="bottom">{description}</Tooltip>
      </TooltipTrigger>

      {enablePredictiveTargetingApi() && isFlagConfiguration && !hideTestRun && (
        <ToggleButtonGroup
          selectionMode="single"
          selectedKeys={testRunEnabled ? ['test-run'] : []}
          data-test-id="schema-editor-mode-button-group"
        >
          <Perceivable isDisabled={hasConflicts}>
            <TooltipTrigger isDisabled={!hasConflicts}>
              <ToggleButton
                size="small"
                onPress={() => {
                  setTestRunEnabled(true);
                  setIsJsonDiffingEnabled(false);
                  trackFlagTargetingEvent('View Test Run Button Clicked', {
                    isApprovalRequest,
                    flagHasEvaluated,
                  });
                }}
                id="test-run"
              >
                <Icon size="tiny" name="fingerprint" />
                {msg('pendingChanges.reviewToolbar.testRun')}
              </ToggleButton>
              <Tooltip placement="bottom">
                <Message id="pendingChanges.reviewToolbar.testRunConflictsTooltip" />
              </Tooltip>
            </TooltipTrigger>
          </Perceivable>
        </ToggleButtonGroup>
      )}
    </ButtonGroup>
  );
}
